import UserInput from "../../input/UserInput";
import Vector from "../../model/math/Vector";
import SkillNode from "../../model/skillTree/SkillNode";
import SkillTreeGraph from "../../model/skillTree/SkillTreeGraph";
import Page from "../framework/Page";
import ParentView from "../framework/ParentView";
import { PageIds } from "../PageIds";
import NodeConnectionView from "./NodeConnectionView";
import SkillNodeView from "./SkillNodeView";

export default class SkillTreeView extends ParentView {
    /**
     * @param {string} parentId
     * @param {SkillTreeGraph} skillTreeGraph
     * @param {UserInput} userInput
     */
    constructor(parentId, skillTreeGraph, userInput) {
        super(parentId, PageIds.SKILL_TREE_GRAPH);

        /**
         * @constant
         * @type {SkillTreeGraph}
         */
        this.skillTreeGraph = skillTreeGraph;

        /**
         * @constant
         * @type {UserInput}
         */
        this.userInput = userInput;

        /**
         * @type {HTMLElement}
         */
        this.mainElement = null;

        /**
         * @constant
         * @type {Vector}
         */
        this.displayCenterOffset = new Vector(-50, -50); // don't initialize to 0,0

        /**
         * @type {number}
         */
        this.displayedZoom = 0;

        const skills = this.skillTreeGraph.skills;
        // Add all the connection views
        for (let i = 0; i < skills.length; i++) {
            this.addNodeConnectionViews(skills[i].rootNode);
        }
        // Add all the node views
        for (let i = 0; i < skills.length; i++) {
            this.addNodeAndChildViews(skills[i].rootNode);
        }
    }

    /**
     * @private
     * @param {SkillNode} node 
     */
    addNodeAndChildViews(node) {
        const id = this.getElementId();

        this.addView(new SkillNodeView(id, node));

        const children = node.children;
        if (!children) {
            return;
        }

        const nodeById = {};

        // Note: Nodes can be the child of multiple other nodes, so we need to make sure not to process a node
        // twice.

        // Non-recursive approach using a queue.
        const queue = [];
        queue.push(...children); // Add all the children to the queue using the 'spread' operator.

        while (queue.length > 0) {
            /**
             * @type {SkillNode}
             */
            const child = queue.shift();

            // Do not process a node more than once.
            if (nodeById[child.id]) {
                continue;
            }
            nodeById[child.id] = true;

            this.addView(new SkillNodeView(id, child));

            if (child.children) {
                for (let i = 0; i < child.children.length; i++) {
                    const node = child.children[i];

                    // Don't add nodes that have already been processed to the queue.
                    if (nodeById[node.id]) {
                        continue;
                    }

                    queue.push(node);
                }
            }
        }
    }

    /**
     * @private
     * @param {SkillNode} rootNode 
     */
    addNodeConnectionViews(rootNode) {
        
        const id = this.getElementId();

        const queue = [];
        queue.push(rootNode);

        // Note: Nodes can be the child of multiple other nodes, so we need to make sure not to process a node
        // twice.

        const nodeById = {};

        while (queue.length > 0) {
            /**
             * @type {SkillNode}
             */
            const current = queue.shift();

            // Make sure we haven't processed the current node yet.
            if (nodeById[current.id]) {
                continue;
            }
            nodeById[current.id] = true;

            const children = current.children;
            if (!children) {
                continue;
            }

            for (let i = 0; i < children.length; i++) {
                const child = children[i];
                this.addView(new NodeConnectionView(id, current, child));

                // Don't add nodes that have already been processed to the queue.
                if (nodeById[child.id] || !child.children) {
                    continue;
                }

                queue.push(child);
            }
        }
    }

    /**
     * Invoked at startup or after a save has been loaded.
     * @override
     */
    initializeView() {

        // <div id="skillTreeGraph" class="skill-tree-graph"></div>
        const parent = Page.getElement(this.parentId);
        this.mainElement = Page.createElement('div', parent, this.elementId, 'skill-tree-graph');

        // This view should start out hidden.
        this.setInitialVisibility(false);

        this.userInput.attachListeners(document, this.mainElement);

        super.initializeView();
    }

    /**
     * @override
     */
    updateViewContents() {

        let updateNodes = false;

        //////////////////////////////////
        // Zoom
        //////////////////////////////////

        const zoom = this.skillTreeGraph.zoom;
        if (this.displayedZoom !== zoom) {
            this.mainElement.style.zoom = zoom + '%';
            this.displayedZoom = zoom;
            updateNodes = true;
        }

        //////////////////////////////////
        // Check if the center has changed
        //////////////////////////////////

        const centerOffset = this.skillTreeGraph.centerOffset;
        if (!this.displayCenterOffset.equals(centerOffset)) {
            this.displayCenterOffset.copy(centerOffset)            
            updateNodes = true;
        }

        if (updateNodes) {
            this.updateNodeCoordinates();
        }

        // Update the children.
        super.updateViewContents();
    }

    /**
     * @private
     */
    updateNodeCoordinates() {
        //////////////////////////////////
        // Pan all nodes
        //////////////////////////////////

        const width = this.mainElement.clientWidth;
        const height = this.mainElement.clientHeight;

        const canvasX = width / 2;
        const canvasY = height / 2;
        const centerOffset = this.skillTreeGraph.centerOffset;

        for (let i = 0; i < this.skillTreeGraph.skills.length; i++) {
            const skill = this.skillTreeGraph.skills[i];
            for (let j = 0; j < skill.nodes.length; j++) {
                const node = skill.nodes[j];

                const nodeX = canvasX + centerOffset.x + node.graphCoordinate.x;
                const nodeY = canvasY + centerOffset.y + node.graphCoordinate.y;
                
                node.viewCoordinate.set(nodeX, nodeY);
            }
        }
    }
}